/**
 * Port of cdc-web `src/utils/abbreviatePersonName.ts`。
 * TwoWayCard 只用 `getPersonInitials` / `getTitleInitials`（fallback badge 文本）；
 * `abbreviatePersonName` / `getDisplayNameAbbrev` 未用到，不抄（simplicity first）。
 * 注：cdc-web 的 abbreviatePersonName 依赖 lodash-es `capitalize`，若后续要抄需注意
 * cdc-market 是否有该依赖。
 */

/**
 * Second-tier (initials-only) abbreviation.
 * @example
 * getPersonInitials("Rico Verhoeven") // "RV"
 * getPersonInitials("", { firstName: "Oleksandr", lastName: "Usyk" }) // "OU"
 * getPersonInitials("Madonna") // "M"
 */
export function getPersonInitials(
  fullName: string,
  parts?: { firstName?: string; lastName?: string },
): string {
  if (parts?.firstName && parts?.lastName) {
    return `${parts.firstName.charAt(0)}${parts.lastName.charAt(0)}`.toUpperCase()
  }

  const words = (fullName ?? '').trim().split(/\s+/).filter(Boolean)
  if (words.length === 0) return fullName ?? ''
  if (words.length === 1) return words[0].charAt(0).toUpperCase()

  const firstInitial = words[0].charAt(0)
  const lastInitial = words[words.length - 1].charAt(0)
  return `${firstInitial}${lastInitial}`.toUpperCase()
}

/**
 * Fallback-badge initials for a no-logo team/country. Multi-word titles take the
 * first letter of every word ("Los Angeles Lakers" → "LAL"); a single-word title
 * takes its first TWO letters ("USA" → "US", "Europe" → "EU") so a country
 * fallback reads as a recognisable code (OG-9916).
 * @example
 * getTitleInitials("Los Angeles Lakers") // "LAL"
 * getTitleInitials("USA") // "US"
 * getTitleInitials("Europe") // "EU"
 */
export function getTitleInitials(title: string): string {
  const words = (title ?? '').trim().split(/\s+/).filter(Boolean)
  if (words.length === 0) return ''
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase()
  return words.map(w => w.charAt(0).toUpperCase()).join('')
}
